import { useEffect } from 'react'

import { useQuery } from 'react-query'
import { useLocation, useNavigate } from 'react-router'
import { Link } from 'react-router-dom'

import { CreatorHeroLogo } from '@/components/layout/header/logo.tsx'
import { Button } from '@/components/ui/button.tsx'
import { Card, CardContent, CardTitle } from '@/components/ui/card.tsx'
import { Skeleton } from '@/components/ui/skeleton.tsx'
import { useAccount } from '@/modules/accounts/hooks/useAccounts'
import { authService } from '@/modules/accounts/service/auth-service.ts'
import { RegisterAfterPaymentForm } from '@/modules/accounts/ui/RegisterAfterPaymentForm'
import { AppRoutes } from '@/utils/constants'
import { classNames } from '@/utils/helpers'
import { useIsDarkTheme } from '@/utils/useTheme'

export const RegisterAfterPaymentPage = () => {
  const { isLoggedIn } = useAccount()

  const isDarkMode = useIsDarkTheme()

  const navigate = useNavigate()

  const location = useLocation()

  const searchParams = new URLSearchParams(location.search)
  const token = searchParams.get('token')
  const emailToken = searchParams.get('emailToken')

  const tokenValue = useQuery(
    ['register-after-payment'],
    () => authService.getRegisterMail(token!),
    { enabled: !!token },
  )

  useEffect(() => {
    if (isLoggedIn) {
      navigate(AppRoutes.Overview.asUrl())
    }
  }, [isLoggedIn, navigate])

  useEffect(() => {
    if (!token) {
      navigate(AppRoutes.Auth.Login.asUrl())
    }
  }, [token, navigate])

  const errorState = () => {
    return (
      <Card className="flex w-full max-w-xl flex-col gap-6 p-8">
        <CardTitle className="flex flex-col items-center justify-center gap-4">
          <CreatorHeroLogo width={215} />
        </CardTitle>
        <CardContent className="grid gap-6 p-0">
          <p className="text-center">
            Oops, you already have set a password or there was an error.
          </p>
          <Link to={AppRoutes.Auth.Login.asUrl()}>
            <Button className="w-full">Go to login</Button>
          </Link>
        </CardContent>
      </Card>
    )
  }

  return (
    <div
      className={classNames(
        "grid h-screen w-screen place-items-center bg-[url('/login-background.webp')] bg-cover bg-no-repeat",
        {
          "bg-[url('/login-background-dark.png')]": isDarkMode,
        },
      )}
    >
      {tokenValue.isError ? (
        errorState()
      ) : (
        <Card className="flex w-full max-w-xl flex-col gap-6 p-8">
          <CardTitle className="flex flex-col items-center justify-center gap-4">
            <CreatorHeroLogo width={215} />
            <div className=" text-sm font-normal text-secondary-300">
              Enter your details to finish the registration
            </div>
          </CardTitle>
          <CardContent className="p-0">
            {tokenValue.isFetching || !token ? (
              <Skeleton className="h-52 w-full" />
            ) : (
              <RegisterAfterPaymentForm
                token={token}
                emailToken={emailToken}
                userMail={tokenValue.data!}
              />
            )}
          </CardContent>
        </Card>
      )}
    </div>
  )
}
